import React, { useEffect, useState, useCallback } from 'react';
import API from '../api';
import { unregisterPushSubscription } from '../push';

const card = {
  background: 'var(--surface, #fff)',
  borderRadius: 16,
  border: '1px solid var(--border, #e2e8f0)',
  padding: '28px 32px',
  marginBottom: 24,
  boxShadow: '0 1px 6px rgba(0,0,0,0.06)',
};

const btn = (variant = 'primary') => ({
  padding: '9px 20px',
  borderRadius: 8,
  border: 'none',
  fontWeight: 600,
  fontSize: 13,
  cursor: 'pointer',
  background: variant === 'primary' ? '#2554b8'
    : variant === 'danger' ? '#ef4444'
    : variant === 'warning' ? '#f59e0b'
    : variant === 'ghost' ? 'transparent'
    : '#f1f5f9',
  color: variant === 'primary' || variant === 'danger' || variant === 'warning' ? '#fff' : '#374151',
  transition: 'opacity 0.15s',
});

const input = {
  width: '100%',
  padding: '9px 12px',
  borderRadius: 8,
  border: '1px solid var(--border, #e2e8f0)',
  fontSize: 14,
  background: 'var(--surface, #fff)',
  color: 'var(--text, #1e293b)',
  outline: 'none',
  boxSizing: 'border-box',
};

const label = { fontSize: 13, fontWeight: 600, color: '#475569', marginBottom: 5, display: 'block' };

function Modal({ title, onClose, children }) {
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', zIndex:2000, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
      <div style={{ background:'var(--surface, #fff)', borderRadius:16, padding:'28px 32px', width:'100%', maxWidth:440, boxShadow:'0 8px 40px rgba(0,0,0,0.18)' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
          <span style={{ fontSize:17, fontWeight:700, color:'var(--text, #1e293b)' }}>{title}</span>
          <button onClick={onClose} style={{ ...btn('ghost'), fontSize:18, padding:'2px 8px', color:'#94a3b8' }}>✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Field({ label: lbl, children }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={label}>{lbl}</label>
      {children}
    </div>
  );
}

export default function SuperAdmin() {
  const name = localStorage.getItem('name') || 'Super Admin';
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Create admin modal
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState({ first_name: '', last_name: '', email: '', password: '' });
  const [createError, setCreateError] = useState('');

  // Delete confirm
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Change own password modal
  const [showChangePw, setShowChangePw] = useState(false);
  const [pwForm, setPwForm] = useState({ old_password: '', new_password: '', confirm_password: '' });
  const [pwError, setPwError] = useState('');
  const [pwSuccess, setPwSuccess] = useState('');
  const [pwLoading, setPwLoading] = useState(false);
  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);

  // Reset admin password modal
  const [resetTarget, setResetTarget] = useState(null);
  const [resetPw, setResetPw] = useState('');
  const [resetConfirm, setResetConfirm] = useState('');
  const [resetError, setResetError] = useState('');
  const [resetSuccess, setResetSuccess] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [showResetPw, setShowResetPw] = useState(false);

  const fetchAdmins = useCallback(() => {
    setLoading(true);
    API.get('/auth/admins')
      .then(r => setAdmins(r.data || []))
      .catch(() => setError('Failed to load admins'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchAdmins(); }, [fetchAdmins]);

  const handleCreate = async () => {
    setCreateError('');
    const { first_name, last_name, email, password } = createForm;
    if (!first_name.trim() || !last_name.trim() || !email.trim() || !password.trim()) {
      setCreateError('All fields are required');
      return;
    }
    if (password.length < 8) {
      setCreateError('Password must be at least 8 characters');
      return;
    }
    setCreating(true);
    try {
      await API.post('/auth/register', {
        first_name: first_name.trim(),
        last_name: last_name.trim(),
        name: `${first_name.trim()} ${last_name.trim()}`,
        email: email.trim(),
        password,
        role: 'admin',
      });
      setShowCreate(false);
      setCreateForm({ first_name: '', last_name: '', email: '', password: '' });
      fetchAdmins();
    } catch (e) {
      setCreateError(e.response?.data?.detail || 'Failed to create admin');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await API.delete(`/auth/admins/${deleteTarget.id}`);
      setDeleteTarget(null);
      fetchAdmins();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to delete admin');
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  const handleChangePw = async () => {
    setPwError(''); setPwSuccess('');
    const { old_password, new_password, confirm_password } = pwForm;
    if (!old_password || !new_password || !confirm_password) {
      setPwError('All fields are required'); return;
    }
    if (new_password !== confirm_password) {
      setPwError('New passwords do not match'); return;
    }
    if (new_password.length < 12) {
      setPwError('New password must be at least 12 characters'); return;
    }
    setPwLoading(true);
    try {
      await API.post('/auth/change-password', { old_password, new_password });
      setPwSuccess('Password changed. Please log in again.');
      setPwForm({ old_password: '', new_password: '', confirm_password: '' });
      setTimeout(() => {
        localStorage.clear();
        sessionStorage.clear();
        window.location.href = '/login';
      }, 2000);
    } catch (e) {
      setPwError(e.response?.data?.detail || 'Failed to change password');
    } finally {
      setPwLoading(false);
    }
  };

  const handleResetAdminPw = async () => {
    setResetError(''); setResetSuccess('');
    if (!resetPw || !resetConfirm) { setResetError('All fields are required'); return; }
    if (resetPw !== resetConfirm) { setResetError('Passwords do not match'); return; }
    if (resetPw.length < 12) { setResetError('Password must be at least 12 characters'); return; }
    setResetLoading(true);
    try {
      await API.post(`/auth/admins/${resetTarget.id}/reset-password`, { new_password: resetPw });
      setResetSuccess(`Password for ${resetTarget.name} has been reset.`);
      setResetPw(''); setResetConfirm('');
      setTimeout(() => { setResetTarget(null); setResetSuccess(''); }, 2000);
    } catch (e) {
      setResetError(e.response?.data?.detail || 'Failed to reset password');
    } finally {
      setResetLoading(false);
    }
  };

  const logout = async () => {
    await unregisterPushSubscription();
    try { await API.post('/auth/logout'); } catch (_) {}
    localStorage.clear();
    sessionStorage.clear();
    window.location.href = '/login';
  };

  return (
    <div style={{ minHeight:'100vh', background:'var(--bg, #f8fafc)', fontFamily:"'Plus Jakarta Sans', sans-serif", color:'var(--text, #1e293b)' }}>
      {/* Header */}
      <div style={{ background:'var(--surface, #fff)', borderBottom:'1px solid var(--border, #e2e8f0)', padding:'0 32px', display:'flex', alignItems:'center', justifyContent:'space-between', height:60, position:'sticky', top:0, zIndex:100 }}>
        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          <div style={{ width:34, height:34, borderRadius:10, background:'linear-gradient(135deg,#2554b8,#6366f1)', display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontWeight:800, fontSize:15 }}>S</div>
          <span style={{ fontWeight:700, fontSize:16, color:'#1e293b' }}>Super Admin</span>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          <span style={{ fontSize:13, color:'#64748b' }}>👋 {name}</span>
          <button onClick={() => { setShowChangePw(true); setPwError(''); setPwSuccess(''); }} style={{ ...btn('secondary'), fontSize:12 }}>Change Password</button>
          <button onClick={logout} style={{ ...btn('danger'), fontSize:12 }}>Logout</button>
        </div>
      </div>

      {/* Main */}
      <div style={{ maxWidth:800, margin:'0 auto', padding:'32px 24px' }}>

        {/* Stats */}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:24 }}>
          {[
            { label: 'Total Admins', value: admins.length, icon: '👤', color: '#2554b8' },
            { label: 'Active Admins', value: admins.filter(a => a.is_active !== 'inactive').length, icon: '✅', color: '#10b981' },
          ].map(s => (
            <div key={s.label} style={{ ...card, marginBottom:0, display:'flex', alignItems:'center', gap:16 }}>
              <div style={{ width:44, height:44, borderRadius:12, background:`${s.color}18`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:20 }}>{s.icon}</div>
              <div>
                <div style={{ fontSize:26, fontWeight:800, color:s.color }}>{s.value}</div>
                <div style={{ fontSize:12, color:'#64748b', fontWeight:600 }}>{s.label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Admin list card */}
        <div style={card}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20 }}>
            <span style={{ fontSize:16, fontWeight:700 }}>Admin Accounts</span>
            <button onClick={() => { setShowCreate(true); setCreateError(''); }} style={btn('primary')}>+ New Admin</button>
          </div>

          {error && <div style={{ background:'#fef2f2', border:'1px solid #fecaca', borderRadius:8, padding:'10px 14px', color:'#dc2626', fontSize:13, marginBottom:14 }}>{error}</div>}

          {loading ? (
            <div style={{ textAlign:'center', color:'#94a3b8', padding:'32px 0', fontSize:14 }}>Loading…</div>
          ) : admins.length === 0 ? (
            <div style={{ textAlign:'center', color:'#94a3b8', padding:'32px 0', fontSize:14 }}>No admin accounts yet. Create one above.</div>
          ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
              {admins.map(a => (
                <div key={a.id} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'12px 16px', borderRadius:10, background:'var(--bg, #f8fafc)', border:'1px solid var(--border, #e2e8f0)' }}>
                  <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                    <div style={{ width:36, height:36, borderRadius:10, background:'#2554b818', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700, color:'#2554b8', fontSize:14 }}>
                      {(a.name || 'A')[0].toUpperCase()}
                    </div>
                    <div>
                      <div style={{ fontWeight:600, fontSize:14 }}>{a.name}</div>
                      <div style={{ fontSize:12, color:'#64748b' }}>{a.email}</div>
                    </div>
                  </div>
                  <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                    <span style={{ fontSize:11, fontWeight:600, padding:'3px 10px', borderRadius:20, background: a.is_active === 'inactive' ? '#fef2f2' : '#f0fdf4', color: a.is_active === 'inactive' ? '#dc2626' : '#16a34a' }}>
                      {a.is_active === 'inactive' ? 'Inactive' : 'Active'}
                    </span>
                    <button
                      onClick={async () => {
                        const next = !a.can_view_profit;
                        if (!window.confirm(`${next ? 'Enable' : 'Disable'} Profit Report access for ${a.name}?`)) return;
                        try {
                          await API.patch(`/auth/users/${a.id}`, { can_view_profit: next });
                          // Refresh list
                          const r = await API.get('/auth/admins');
                          setAdmins(r.data || []);
                        } catch (e) { alert(e?.response?.data?.detail || 'Failed to update permission'); }
                      }}
                      title={a.can_view_profit ? 'Click to disable profit report access' : 'Click to enable profit report access for this admin'}
                      style={{ fontSize:11, fontWeight:700, padding:'5px 12px', borderRadius:20, border:'1px solid', cursor:'pointer',
                        borderColor: a.can_view_profit ? '#16a34a' : '#cbd5e1',
                        background: a.can_view_profit ? '#dcfce7' : '#fff',
                        color: a.can_view_profit ? '#15803d' : '#64748b' }}>
                      💹 Profit {a.can_view_profit ? 'ON' : 'OFF'}
                    </button>
                    <button
                      onClick={async () => {
                        const next = !a.dashboard_active_only;
                        if (!window.confirm(`${next ? 'Show ACTIVE-only' : 'Show ALL'} student & companion counts on ${a.name}'s dashboard?`)) return;
                        try {
                          await API.patch(`/auth/users/${a.id}`, { dashboard_active_only: next });
                          const r = await API.get('/auth/admins');
                          setAdmins(r.data || []);
                        } catch (e) { alert(e?.response?.data?.detail || 'Failed to update preference'); }
                      }}
                      title={a.dashboard_active_only ? 'Dashboard shows ACTIVE-only counts — click to show all' : 'Dashboard shows all counts — click to show active-only'}
                      style={{ fontSize:11, fontWeight:700, padding:'5px 12px', borderRadius:20, border:'1px solid', cursor:'pointer',
                        borderColor: a.dashboard_active_only ? '#2554b8' : '#cbd5e1',
                        background: a.dashboard_active_only ? '#dbeafe' : '#fff',
                        color: a.dashboard_active_only ? '#1e40af' : '#64748b' }}>
                      📊 Active-only {a.dashboard_active_only ? 'ON' : 'OFF'}
                    </button>
                    <button onClick={() => { setResetTarget(a); setResetPw(''); setResetConfirm(''); setResetError(''); setResetSuccess(''); }} style={{ ...btn('warning'), padding:'6px 14px', fontSize:12 }}>Reset Password</button>
                    <button onClick={() => setDeleteTarget(a)} style={{ ...btn('danger'), padding:'6px 14px', fontSize:12 }}>Delete</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Create Admin Modal */}
      {showCreate && (
        <Modal title="Create New Admin" onClose={() => setShowCreate(false)}>
          <Field label="First Name">
            <input style={input} value={createForm.first_name} onChange={e => setCreateForm(f => ({ ...f, first_name: e.target.value }))} placeholder="First name" />
          </Field>
          <Field label="Last Name">
            <input style={input} value={createForm.last_name} onChange={e => setCreateForm(f => ({ ...f, last_name: e.target.value }))} placeholder="Last name" />
          </Field>
          <Field label="Email">
            <input style={input} type="email" value={createForm.email} onChange={e => setCreateForm(f => ({ ...f, email: e.target.value }))} placeholder="admin@email.com" />
          </Field>
          <Field label="Password">
            <input style={input} type="password" value={createForm.password} onChange={e => setCreateForm(f => ({ ...f, password: e.target.value }))} placeholder="Min 8 characters" />
          </Field>
          {createError && <div style={{ color:'#dc2626', fontSize:13, marginBottom:12 }}>{createError}</div>}
          <div style={{ display:'flex', gap:10, justifyContent:'flex-end', marginTop:8 }}>
            <button onClick={() => setShowCreate(false)} style={btn('secondary')}>Cancel</button>
            <button onClick={handleCreate} disabled={creating} style={{ ...btn('primary'), opacity: creating ? 0.6 : 1 }}>
              {creating ? 'Creating…' : 'Create Admin'}
            </button>
          </div>
        </Modal>
      )}

      {/* Delete Confirm Modal */}
      {deleteTarget && (
        <Modal title="Delete Admin" onClose={() => setDeleteTarget(null)}>
          <p style={{ fontSize:14, color:'#475569', marginBottom:20 }}>
            Are you sure you want to delete <strong>{deleteTarget.name}</strong>? This cannot be undone.
          </p>
          <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
            <button onClick={() => setDeleteTarget(null)} style={btn('secondary')}>Cancel</button>
            <button onClick={handleDelete} disabled={deleting} style={{ ...btn('danger'), opacity: deleting ? 0.6 : 1 }}>
              {deleting ? 'Deleting…' : 'Yes, Delete'}
            </button>
          </div>
        </Modal>
      )}

      {/* Reset Admin Password Modal */}
      {resetTarget && (
        <Modal title={`Reset Password — ${resetTarget.name}`} onClose={() => setResetTarget(null)}>
          <div style={{ background:'#fffbeb', border:'1px solid #fde68a', borderRadius:8, padding:'10px 14px', fontSize:12, color:'#92400e', marginBottom:16 }}>
            This will immediately invalidate the admin's current session and force them to log in again.
          </div>
          <Field label="New Password">
            <input style={input} type={showResetPw ? 'text' : 'password'} value={resetPw} onChange={e => setResetPw(e.target.value)} placeholder="Min 12 chars, upper+lower+digit+special" />
          </Field>
          <Field label="Confirm New Password">
            <input style={input} type={showResetPw ? 'text' : 'password'} value={resetConfirm} onChange={e => setResetConfirm(e.target.value)} placeholder="Repeat new password" />
          </Field>
          <div style={{ marginBottom:12 }}>
            <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:12, color:'#64748b', cursor:'pointer' }}>
              <input type="checkbox" checked={showResetPw} onChange={e => setShowResetPw(e.target.checked)} />
              Show passwords
            </label>
          </div>
          {resetError && <div style={{ color:'#dc2626', fontSize:13, marginBottom:12 }}>{resetError}</div>}
          {resetSuccess && <div style={{ color:'#16a34a', fontSize:13, marginBottom:12 }}>{resetSuccess}</div>}
          <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
            <button onClick={() => setResetTarget(null)} style={btn('secondary')}>Cancel</button>
            <button onClick={handleResetAdminPw} disabled={resetLoading} style={{ ...btn('primary'), opacity: resetLoading ? 0.6 : 1 }}>
              {resetLoading ? 'Saving…' : 'Reset Password'}
            </button>
          </div>
        </Modal>
      )}

      {/* Change Own Password Modal */}
      {showChangePw && (
        <Modal title="Change Password" onClose={() => setShowChangePw(false)}>
          <Field label="Current Password">
            <div style={{ position:'relative' }}>
              <input style={input} type={showOld ? 'text' : 'password'} value={pwForm.old_password} onChange={e => setPwForm(f => ({ ...f, old_password: e.target.value }))} placeholder="Current password" />
              <button onClick={() => setShowOld(v => !v)} style={{ position:'absolute', right:10, top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', color:'#94a3b8', fontSize:13 }}>{showOld ? 'Hide' : 'Show'}</button>
            </div>
          </Field>
          <Field label="New Password">
            <div style={{ position:'relative' }}>
              <input style={input} type={showNew ? 'text' : 'password'} value={pwForm.new_password} onChange={e => setPwForm(f => ({ ...f, new_password: e.target.value }))} placeholder="Min 12 chars, upper+lower+digit+special" />
              <button onClick={() => setShowNew(v => !v)} style={{ position:'absolute', right:10, top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', color:'#94a3b8', fontSize:13 }}>{showNew ? 'Hide' : 'Show'}</button>
            </div>
          </Field>
          <Field label="Confirm New Password">
            <input style={input} type="password" value={pwForm.confirm_password} onChange={e => setPwForm(f => ({ ...f, confirm_password: e.target.value }))} placeholder="Repeat new password" />
          </Field>
          {pwError && <div style={{ color:'#dc2626', fontSize:13, marginBottom:12 }}>{pwError}</div>}
          {pwSuccess && <div style={{ color:'#16a34a', fontSize:13, marginBottom:12 }}>{pwSuccess}</div>}
          <div style={{ background:'#fffbeb', border:'1px solid #fde68a', borderRadius:8, padding:'10px 14px', fontSize:12, color:'#92400e', marginBottom:16 }}>
            After changing password you will be logged out automatically.
          </div>
          <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
            <button onClick={() => setShowChangePw(false)} style={btn('secondary')}>Cancel</button>
            <button onClick={handleChangePw} disabled={pwLoading} style={{ ...btn('primary'), opacity: pwLoading ? 0.6 : 1 }}>
              {pwLoading ? 'Saving…' : 'Change Password'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
